import React from 'react';

const STEP_LABELS = {
  register: 'Registration',
  contact: 'Email / Phone',
  id: 'ID Verification',
};

function DocumentIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 3.75h6l3 3v13.5A1.5 1.5 0 0 1 16.5 21.75h-9A1.5 1.5 0 0 1 6 20.25V5.25A1.5 1.5 0 0 1 7.5 3.75H9Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75h4.5M9.75 13.5h4.5M9.75 17.25h3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 3.75v3h3" />
    </svg>
  );
}

function MailIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 7.5A2.25 2.25 0 0 1 6 5.25h12A2.25 2.25 0 0 1 20.25 7.5v9A2.25 2.25 0 0 1 18 18.75H6A2.25 2.25 0 0 1 3.75 16.5v-9Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 8.25 6.64 5.08a1.5 1.5 0 0 0 1.82 0l6.64-5.08" />
    </svg>
  );
}

function PhoneIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 2.25h9A1.5 1.5 0 0 1 18 3.75v16.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 20.25V3.75a1.5 1.5 0 0 1 1.5-1.5Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 18h3" />
    </svg>
  );
}

function IdIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <rect x="3.75" y="5.25" width="16.5" height="13.5" rx="2.25" />
      <circle cx="8.5" cy="10.25" r="1.75" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 14.25c.86-1.08 2-1.62 3.41-1.62 1.43 0 2.58.54 3.46 1.62M13.5 9.75h4.5M13.5 13.5h4.5" />
    </svg>
  );
}

function StepBubble({ completed, active, tone, label, icon }) {
  const activeClasses = tone === 'dark'
    ? 'border-blue-400 bg-blue-500 text-white shadow-[0_10px_24px_rgba(37,99,235,0.35)]'
    : 'border-blue-600 bg-blue-600 text-white shadow-[0_12px_24px_rgba(37,99,235,0.18)]';
  const completedClasses = tone === 'dark'
    ? 'border-blue-300/70 bg-blue-500/15 text-blue-100'
    : 'border-blue-200 bg-blue-50 text-blue-700';
  const inactiveClasses = tone === 'dark'
    ? 'border-white/20 bg-white/10 text-white/60'
    : 'border-slate-200 bg-slate-100 text-slate-400';
  const labelClasses = tone === 'dark'
    ? active || completed ? 'text-white' : 'text-white/65'
    : active ? 'text-slate-900' : completed ? 'text-slate-700' : 'text-slate-400';

  return (
    <div className="w-[5.25rem] flex-shrink-0 text-center">
      <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full border transition-colors ${active ? activeClasses : completed ? completedClasses : inactiveClasses}`}>
        {icon}
      </div>
      <p className={`mt-2 text-[11px] font-medium leading-4 ${labelClasses}`}>
        {label}
      </p>
    </div>
  );
}

function ContactBubble({ currentStep, tone }) {
  const isEmailActive = currentStep === 'email';
  const isPhoneActive = currentStep === 'phone';
  const isComplete = currentStep === 'id';
  const shellClasses = tone === 'dark'
    ? 'border-white/20 bg-white/10'
    : 'border-slate-200 bg-slate-100';
  const inactiveHalfClasses = tone === 'dark'
    ? 'bg-transparent text-white/55'
    : 'bg-transparent text-slate-400';
  const completeHalfClasses = tone === 'dark'
    ? 'bg-blue-500/15 text-blue-100'
    : 'bg-blue-50 text-blue-700';
  const activeHalfClasses = tone === 'dark'
    ? 'bg-blue-500 text-white'
    : 'bg-blue-600 text-white';
  const labelClasses = tone === 'dark'
    ? isEmailActive || isPhoneActive || isComplete ? 'text-white' : 'text-white/65'
    : isEmailActive || isPhoneActive ? 'text-slate-900' : isComplete ? 'text-slate-700' : 'text-slate-400';

  const emailHalfClasses = isEmailActive
    ? activeHalfClasses
    : isPhoneActive || isComplete
      ? completeHalfClasses
      : inactiveHalfClasses;
  const phoneHalfClasses = isPhoneActive
    ? activeHalfClasses
    : isComplete
      ? completeHalfClasses
      : inactiveHalfClasses;

  return (
    <div className="w-[6rem] flex-shrink-0 text-center">
      <div className={`mx-auto flex h-12 w-14 overflow-hidden rounded-full border transition-colors ${shellClasses}`}>
        <div className={`flex flex-1 items-center justify-center border-r ${tone === 'dark' ? 'border-white/10' : 'border-slate-200'} transition-colors ${emailHalfClasses}`}>
          <MailIcon className="h-5 w-5" />
        </div>
        <div className={`flex flex-1 items-center justify-center transition-colors ${phoneHalfClasses}`}>
          <PhoneIcon className="h-5 w-5" />
        </div>
      </div>
      <p className={`mt-2 text-[11px] font-medium leading-4 ${labelClasses}`}>
        {STEP_LABELS.contact}
      </p>
    </div>
  );
}

function Connector({ active, tone }) {
  const trackClasses = tone === 'dark' ? 'bg-white/10' : 'bg-slate-200';
  const fillClasses = tone === 'dark' ? 'bg-blue-400' : 'bg-blue-600';

  return (
    <div className="mt-6 h-1 flex-1 overflow-hidden rounded-full">
      <div className={`h-full w-full ${trackClasses}`}>
        <div className={`h-full transition-all ${fillClasses}`} style={{ width: active ? '100%' : '0%' }} />
      </div>
    </div>
  );
}

export default function RegistrationProgress({ currentStep, tone = 'light', className = '' }) {
  const registerCompleted = currentStep !== 'register';
  const contactConnectorActive = currentStep === 'id';

  return (
    <div className={`rounded-2xl border px-3 py-4 sm:px-4 ${tone === 'dark' ? 'border-white/20 bg-white/5' : 'border-slate-200 bg-white'} ${className}`}>
      <div className="flex items-start gap-2">
        <StepBubble
          completed={registerCompleted}
          active={currentStep === 'register'}
          tone={tone}
          label={STEP_LABELS.register}
          icon={<DocumentIcon className="h-5 w-5" />}
        />
        <Connector active={registerCompleted} tone={tone} />
        <ContactBubble currentStep={currentStep} tone={tone} />
        <Connector active={contactConnectorActive} tone={tone} />
        <StepBubble
          completed={false}
          active={currentStep === 'id'}
          tone={tone}
          label={STEP_LABELS.id}
          icon={<IdIcon className="h-5 w-5" />}
        />
      </div>
    </div>
  );
}
